import React, { useState, useEffect } from 'react';
import api from 'api';
import HeroContentType from 'containers/user/learn/components/hero-content-type/hero-content-type';
import NothingFound from 'containers/user/learn/components/empty-row/empty-row';
import Cards from 'containers/user/learn/components/cards/cards';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';
import SeeMoreButton from 'containers/user/learn/components/see-more-btn/see-more-btn';
import { useCurrentUser } from 'hooks/api/use-current-user';

interface GuideCardItem {
  name?: string;
  route: string;
  coverDescription?: string;
  coverImage?: string;
  publishedAt?: string;
}

interface GuideApiEntry {
  id: string | number;
  attributes?: {
    name?: string;
    coverDescription?: string;
    coverImage?: { data?: { attributes?: { url?: string } } };
    publishedAt?: string;
  };
}

const Guides = (): React.JSX.Element => {
  const [guides, setGuides] = useState<GuideCardItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [displayAll, setDisplayAll] = useState<boolean>(false);

  const user = useCurrentUser();

  useEffect(() => {
    setLoading(true);
    api
      .getGuides(user?.data?.oktaUserId ?? '')
      .then((res) => {
        const data = (res as { data?: { data?: GuideApiEntry[] } })?.data?.data;
        const sortedGuides = data
          ?.map((el) => ({
            name: el.attributes?.name,
            route: `/learn/content/guides/${el.id}`,
            coverDescription: el.attributes?.coverDescription,
            coverImage: el.attributes?.coverImage?.data?.attributes?.url,
            publishedAt: el.attributes?.publishedAt,
          }))
          .sort((a, b) => {
            const dateA = a.publishedAt ? new Date(a.publishedAt) : new Date(0);
            const dateB = b.publishedAt ? new Date(b.publishedAt) : new Date(0);

            return dateB.getTime() - dateA.getTime();
          });

        setGuides(sortedGuides || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const displayedGuides = displayAll ? guides : guides.slice(0, 3);

  return (
    <Box sx={{ width: '100%' }}>
      <Loader loading={loading} />
      <HeroContentType
        title="Guides"
        description="Check out a collection of guides and tutorials created by MARKETview partners for MARKETview partners."
        breadcrumbs={[
          { label: 'Learn', href: '/learn' },
          { label: 'Guides', isActive: true },
        ]}
        customClass="pt-5"
      />
      <Stack sx={{ alignItems: 'center', mt: 5 }}>
        {!displayedGuides.length && !loading ? (
          <NothingFound contentType="guides" />
        ) : (
          <Cards items={displayedGuides} contentType="Guide" />
        )}

        {guides.length > 3 && (
          <SeeMoreButton
            onClick={() => setDisplayAll(!displayAll)}
            showAll={displayAll}
          />
        )}
      </Stack>
    </Box>
  );
};

export default Guides;
